import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { updateGradebook } from '../../../../actions/gradebook';
import { DEFAULT_PAGE_SIZE } from '../components/Gradebook/constants';
import {
  resetInstructorGradebookSessionPageSize,
  setInstructorGradebookSessionPageSize,
} from '../instructorGradebookPageSizeSession';

const GradebookPageSizeContext = createContext(null);

export function GradebookPageSizeProvider({
  dispatch,
  initialPageSize,
  children,
}) {
  const [pageSize, setPageSizeState] = useState(
    initialPageSize ?? DEFAULT_PAGE_SIZE,
  );

  const setPageSize = useCallback((nextPageSize) => {
    setInstructorGradebookSessionPageSize(nextPageSize);
    setPageSizeState(nextPageSize);
  }, []);

  useEffect(() => {
    if (initialPageSize != null) {
      setPageSizeState(initialPageSize);
      setInstructorGradebookSessionPageSize(initialPageSize);
    }
  }, [initialPageSize]);

  useEffect(() => {
    setInstructorGradebookSessionPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    return () => {
      resetInstructorGradebookSessionPageSize();
      if (dispatch) {
        dispatch(
          updateGradebook({
            limit: DEFAULT_PAGE_SIZE,
            page: 1,
          }),
        );
      }
    };
  }, [dispatch]);

  const value = useMemo(
    () => ({
      pageSize,
      setPageSize,
    }),
    [pageSize, setPageSize],
  );

  return (
    <GradebookPageSizeContext.Provider value={value}>
      {children}
    </GradebookPageSizeContext.Provider>
  );
}

GradebookPageSizeProvider.propTypes = {
  dispatch: PropTypes.func,
  initialPageSize: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export function useGradebookPageSize() {
  const context = useContext(GradebookPageSizeContext);
  if (!context) {
    throw new Error(
      'useGradebookPageSize must be used within a GradebookPageSizeProvider',
    );
  }
  return context;
}

export function useGradebookPageSizeOptional() {
  return useContext(GradebookPageSizeContext);
}
